"use client";

import { useEffect, useState } from "react";

import { Checkbox } from "~/components/ui/checkbox";
import { swatchBackground } from "~/lib/colour-swatch";
import { formatMoney } from "~/lib/money";
import { cn } from "~/lib/utils";
import type { FacetGroupModel } from "./facet-model";
import { useTranslator } from "~/i18n/provider";

/**
 * P2-11 · The facet controls, driven by a handler bag so the same rendering
 * works live (desktop rail → URL) and staged (mobile drawer → local state).
 */
export interface FacetHandlers {
  isSelected(param: string, value: string): boolean;
  /**
   * P15-20 · Widened from the three fixed params to any multi-value param —
   * a dynamic group's is `attr.<slug>`, which no closed union can name. The
   * URL helper never cared: it has always been a comma-list edit.
   */
  toggle(param: string, value: string): void;
  setFlag(param: "sale" | "availability", on: boolean): void;
  setPrice(loMajor: number | undefined, hiMajor: number | undefined): void;
  setAttributeRange(param: string, lo: number | undefined, hi: number | undefined): void;
  attributeRange(param: string): { lo?: number; hi?: number };
  price: { loMajor?: number; hiMajor?: number };
  currency: string;
}

export function FacetGroups({
  groups,
  handlers,
}: {
  groups: FacetGroupModel[];
  handlers: FacetHandlers;
}) {
  return (
    <div className="flex flex-col divide-y divide-border">
      {groups.map((group) => (
        <FacetGroup key={group.key} group={group} handlers={handlers} />
      ))}
    </div>
  );
}

function FacetGroup({
  group,
  handlers,
}: {
  group: FacetGroupModel;
  handlers: FacetHandlers;
}) {
  return (
    <details open className="group py-4 first:pt-0">
      <summary className="flex cursor-pointer list-none items-center justify-between font-medium text-fg [&::-webkit-details-marker]:hidden">
        {group.title}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          className="h-4 w-4 text-fg-muted transition-transform group-open:rotate-180"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </summary>
      <fieldset className="mt-3 border-0 p-0">
        <legend className="sr-only">{group.title}</legend>
        {group.kind === "price" ? (
          <PriceFacet group={group} handlers={handlers} />
        ) : group.kind === "range" ? (
          <NumberAttributeFacet group={group} handlers={handlers} />
        ) : group.kind === "swatch" ? (
          <SwatchFacet group={group} handlers={handlers} />
        ) : group.kind === "toggle" ? (
          <ToggleFacet group={group} handlers={handlers} />
        ) : (
          <CheckboxFacet group={group} handlers={handlers} />
        )}
      </fieldset>
    </details>
  );
}

function NumberAttributeFacet({
  group,
  handlers,
}: {
  group: FacetGroupModel;
  handlers: FacetHandlers;
}) {
  const bounds = group.numberBounds;
  const applied = handlers.attributeRange(group.param);
  const [lo, setLo] = useState(applied.lo?.toString() ?? "");
  const [hi, setHi] = useState(applied.hi?.toString() ?? "");
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setLo(applied.lo?.toString() ?? "");
    setHi(applied.hi?.toString() ?? "");
    setInvalid(false);
  }, [applied.lo, applied.hi]);

  const commit = () => {
    const loNumber = lo.trim() === "" ? undefined : Number(lo);
    const hiNumber = hi.trim() === "" ? undefined : Number(hi);
    if (
      (loNumber !== undefined && !Number.isFinite(loNumber)) ||
      (hiNumber !== undefined && !Number.isFinite(hiNumber)) ||
      (loNumber !== undefined && hiNumber !== undefined && loNumber > hiNumber)
    ) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    handlers.setAttributeRange(group.param, loNumber, hiNumber);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <label className="sr-only" htmlFor={`${group.key}-min`}>Minimum {group.title}</label>
        <input
          id={`${group.key}-min`}
          type="number"
          inputMode="decimal"
          step="any"
          aria-label={`Minimum ${group.title}`}
          aria-invalid={invalid || undefined}
          placeholder={bounds ? String(bounds.min) : "Min"}
          value={lo}
          onChange={(event) => setLo(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => event.key === "Enter" && commit()}
          className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] aria-[invalid=true]:border-danger"
        />
        <span className="text-fg-muted">–</span>
        <label className="sr-only" htmlFor={`${group.key}-max`}>Maximum {group.title}</label>
        <input
          id={`${group.key}-max`}
          type="number"
          inputMode="decimal"
          step="any"
          aria-label={`Maximum ${group.title}`}
          aria-invalid={invalid || undefined}
          placeholder={bounds ? String(bounds.max) : "Max"}
          value={hi}
          onChange={(event) => setHi(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => event.key === "Enter" && commit()}
          className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] aria-[invalid=true]:border-danger"
        />
      </div>
      {bounds ? (
        <p className="text-xs text-fg-muted">Available: {bounds.min}–{bounds.max}{bounds.unit ? ` ${bounds.unit}` : ""}</p>
      ) : null}
      {invalid ? <p role="alert" className="text-xs text-danger">Minimum must not exceed maximum.</p> : null}
    </div>
  );
}

/**
 * P15-20 · Values rendered before "+N more" folds the rest away. Eight is what
 * fits the 240px rail without turning a facet into a scroll region; a brand's
 * "Material" with thirty values must not push "Availability" off the screen.
 */
const VISIBLE_VALUES = 8;

function CheckboxFacet({
  group,
  handlers,
}: {
  group: FacetGroupModel;
  handlers: FacetHandlers;
}) {
  const t = useTranslator();
  const [expanded, setExpanded] = useState(false);
  // Values the server could not carry (past the cap) — never reachable by
  // expanding, so they are counted in "+N more" and named when it is spent.
  const uncarried = group.hiddenCount ?? 0;
  const folded = expanded ? [] : group.options.slice(VISIBLE_VALUES);
  const shown = expanded ? group.options : group.options.slice(0, VISIBLE_VALUES);
  return (
    <ul className="flex flex-col gap-2">
      {shown.map((o) => (
        <li key={o.value}>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-fg">
            <Checkbox
              checked={handlers.isSelected(group.param, o.value)}
              onChange={() => handlers.toggle(group.param, o.value)}
            />
            <span className="flex-1">{o.labelKey ? t(o.labelKey) : o.label}</span>
            <span className="text-xs text-fg-muted">{o.count}</span>
          </label>
        </li>
      ))}
      {folded.length > 0 && (
        <li>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-sm text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {/* The count includes what expanding cannot reveal, so the number
                is the number of values this group is not showing — not a
                promise about how many rows the click will add. */}
            {t("browse.facetShowMore", { count: folded.length + uncarried })}
          </button>
        </li>
      )}
      {expanded && (
        <li className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-sm text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {t("browse.facetShowLess")}
          </button>
          {uncarried > 0 && (
            <span className="text-xs text-fg-muted">
              {t("browse.facetMoreHidden", { count: uncarried })}
            </span>
          )}
        </li>
      )}
      {!expanded && folded.length === 0 && uncarried > 0 && (
        <li className="text-xs text-fg-muted">
          {t("browse.facetMoreHidden", { count: uncarried })}
        </li>
      )}
    </ul>
  );
}

function SwatchFacet({
  group,
  handlers,
}: {
  group: FacetGroupModel;
  handlers: FacetHandlers;
}) {
  return (
    <ul className="flex flex-wrap gap-2">
      {group.options.map((o) => {
        const selected = handlers.isSelected(group.param, o.value);
        return (
          <li key={o.value}>
            <button
              type="button"
              aria-pressed={selected}
              aria-label={`${o.label} (${o.count})`}
              title={`${o.label} · ${o.count}`}
              onClick={() => handlers.toggle("color", o.value)}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
                selected
                  ? "border-fg ring-2 ring-fg ring-offset-2 ring-offset-surface"
                  : "border-border",
              )}
              style={{
                background:
                  swatchBackground(o.value, o.hex) ?? "var(--color-surface-2)",
              }}
            >
              {selected && (
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  className="h-4 w-4 text-surface mix-blend-difference"
                >
                  <path d="m5 13 4 4L19 7" />
                </svg>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ToggleFacet({
  group,
  handlers,
}: {
  group: FacetGroupModel;
  handlers: FacetHandlers;
}) {
  const param = group.param as "sale" | "availability";
  const o = group.options[0];
  if (!o) return null;
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-fg">
      <Checkbox
        checked={handlers.isSelected(group.param, o.value)}
        onChange={(e) => handlers.setFlag(param, e.target.checked)}
      />
      <span className="flex-1">{o.label}</span>
      <span className="text-xs text-fg-muted">{o.count}</span>
    </label>
  );
}

function PriceFacet({
  group,
  handlers,
}: {
  group: FacetGroupModel;
  handlers: FacetHandlers;
}) {
  const t = useTranslator();
  const boundsLo = group.bounds?.minMinor ?? null;
  const boundsHi = group.bounds?.maxMinor ?? null;
  const [lo, setLo] = useState(handlers.price.loMajor?.toString() ?? "");
  const [hi, setHi] = useState(handlers.price.hiMajor?.toString() ?? "");

  // Re-seed inputs when the applied band changes elsewhere (chip removal, etc.).
  useEffect(() => {
    setLo(handlers.price.loMajor?.toString() ?? "");
    setHi(handlers.price.hiMajor?.toString() ?? "");
  }, [handlers.price.loMajor, handlers.price.hiMajor]);

  const commit = () => {
    const loNum = lo.trim() === "" ? undefined : Number(lo);
    const hiNum = hi.trim() === "" ? undefined : Number(hi);
    handlers.setPrice(
      loNum !== undefined && Number.isFinite(loNum) ? loNum : undefined,
      hiNum !== undefined && Number.isFinite(hiNum) ? hiNum : undefined,
    );
  };

  const fmt = (m: number | null) =>
    m === null ? "" : formatMoney(m, handlers.currency);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input
          type="number"
          inputMode="numeric"
          min={0}
          aria-label={t("browse.minPrice")}
          placeholder={
            boundsLo !== null ? String(Math.floor(boundsLo / 100)) : "Min"
          }
          value={lo}
          onChange={(e) => setLo(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === "Enter" && commit()}
          className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        />
        <span className="text-fg-muted">–</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          aria-label={t("browse.maxPrice")}
          placeholder={
            boundsHi !== null ? String(Math.ceil(boundsHi / 100)) : "Max"
          }
          value={hi}
          onChange={(e) => setHi(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === "Enter" && commit()}
          className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        />
      </div>
      {boundsLo !== null && boundsHi !== null && (
        <p className="text-xs text-fg-muted">
          {fmt(boundsLo)} – {fmt(boundsHi)}
        </p>
      )}
    </div>
  );
}
